import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
    Aangifte,
    BokkzAlgDataService,
    BokkzIbDataService,
    IncomeTaxEntity,
    Onderneming,
    Ondernemingsvorm
} from '@snelstart-ventures/fiscaal/domain-logic';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { BehaviorSubject, filter, Subscription } from 'rxjs';
import { ToastService } from '@snelstart/ui-toast';
import { Event, NavigationEnd, Router } from '@angular/router';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDialogModule } from '@snelstart/ui-dialog';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-algemeen',
    templateUrl: './algemeen.component.html',
    styleUrls: ['./algemeen.component.scss'],
    imports: [
        SharedUiDetailsModule,
        SharedUiFormControlsModule,
        SharedUiButtonModule,
        SharedUiDialogModule,
        CommonModule
    ]
})
export class AlgemeenComponent implements OnInit, OnDestroy {
    @Input() public id!: string;
    public clientId!: string;

    private routerSubscription: Subscription | undefined;
    private incomeTaxEntitySubscription: Subscription | undefined;
    private clientEntitySubscription: Subscription | undefined;
    private isSaving = false;

    public aangifte = {} as Aangifte;
    public incomeTaxEntity = {} as IncomeTaxEntity;

    public partnerBoolean = {} as boolean;
    @Input() partner = new BehaviorSubject<boolean>(false);
    private partnerAangifte = {} as Aangifte;

    private focusedElementId: string | null = null;
    public dialogVisible = false;
    public showSpinner = false;

    public openInfoVerwijderen = false;

    public ondernemingFG = {} as FormGroup;

    public optionsJaNee: SelectFormControlItem<boolean | null>[] = [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nee' },
    ];
    public optionsJa: SelectFormControlItem<boolean | null>[] = [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nee', disabled: true },
    ];

    public ondernemingsvorm: SelectFormControlItem<string>[] = [
        { value: Ondernemingsvorm.EENMANSZAAK, label: 'Eenmanszaak' },
        { value: Ondernemingsvorm.VOF, label: 'Vennootschap onder firma (VOF)' },
        { value: Ondernemingsvorm.MAATSCHAP, label: 'Maatschap' },
        {
            value: Ondernemingsvorm.CV,
            label: 'Commanditaire vennootschap (CV) - nog niet beschikbaar',
            disabled: true
        }];

    public showDialog() {
        this.dialogVisible = true;
    }

    constructor(private fb: FormBuilder, private router: Router, public dataService: BokkzIbDataService, public dataAlgService: BokkzAlgDataService,
                public toastService: ToastService) {
        this.ondernemingFG = this.fb.group({
            ondernemingen: this.fb.array([this.createOndernemingFormGroup()])
        });
    }

    get ondernemingen(): FormArray {
        return this.ondernemingFG.get('ondernemingen') as FormArray;
    }

    addOnderneming(): void {
        const onderneming = this.createOndernemingFormGroup();
        this.ondernemingen.push(onderneming);
    }

    verwijderOnderneming(index: number): void {
        if (this.partnerBoolean) {
            this.ondernemingen.removeAt(index);
            const ondernemingen = this.incomeTaxEntity.aangifte?.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen;
            if (ondernemingen && index >= 0 && index < ondernemingen.length) {
                ondernemingen.splice(index, 1);
                const box1 = this.incomeTaxEntity.aangifte?.partnerAangifte?.box1;
                if (box1) {
                    box1.winstUitOnderneming = undefined;
                }
                this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
                this.toastService.showSuccessMessage('De gegevens van de onderneming zijn succesvol verwijderd.');
                this.ondernemingFG.updateValueAndValidity();
                this.ondernemingFG.markAsDirty();
            }
        } else {
            this.ondernemingen.removeAt(index);
            const ondernemingen = this.incomeTaxEntity.aangifte?.box1?.winstUitOnderneming?.ondernemingen;
            if (ondernemingen && index >= 0 && index < ondernemingen.length) {
                ondernemingen.splice(index, 1);
                const box1 = this.incomeTaxEntity.aangifte?.box1;
                if (box1) {
                    box1.winstUitOnderneming = undefined;
                }
                this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
                this.toastService.showSuccessMessage('De gegevens van de onderneming zijn succesvol verwijderd.');
            }
            this.ondernemingFG.updateValueAndValidity();
            this.ondernemingFG.markAsDirty();
        }
    }

    showInfoVerwijderen() {
        this.openInfoVerwijderen = !this.openInfoVerwijderen;
    }

    createOndernemingFormGroup(onderneming?: Onderneming) {
        return this.dataService.createOndernemingFormGroup(onderneming);
    }

    public ngOnInit(): void {
        this.incomeTaxEntitySubscription = this.dataService.incomeTaxEntity.subscribe((incomeTaxEntity) => {
            this.incomeTaxEntity = incomeTaxEntity;
            if (incomeTaxEntity.aangifte) {
                const aangifte = incomeTaxEntity.aangifte as Aangifte;
                this.aangifte = aangifte;
                this.partnerAangifte = aangifte.partnerAangifte ? aangifte.partnerAangifte : {} as Aangifte;
                this.updateForm();
                this.partner.subscribe((boolean) => {
                    this.partnerBoolean = boolean;
                    this.ondernemingFG.reset();
                    this.updateForm();
                });
            }
        });
        this.clientEntitySubscription = this.dataAlgService.client.subscribe((client) => {
            this.clientId = client.sstRelationId!;
        });
        this.routerSubscription = this.router.events.pipe(filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.handleUrlChange(event.urlAfterRedirects);
            });

    }

    ngOnDestroy(): void {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
        }
        if (this.incomeTaxEntitySubscription) {
            this.incomeTaxEntitySubscription.unsubscribe();
        }
        if (this.clientEntitySubscription) {
            this.clientEntitySubscription.unsubscribe();
        }
    }

    handleUrlChange(newUrl: string) {
        if (!this.isSaving && this.hasUnsavedChanges()) {
            this.isSaving = true;
            this.dataService.postIncomeTaxEntity(this.clientId, false, this.incomeTaxEntity);
            this.isSaving = false;
        }
    }

    private hasUnsavedChanges(): boolean {
        return this.ondernemingFG.dirty;
    }

    private updateForm(): void {
        let ondernemingenLijst = this.aangifte?.box1?.winstUitOnderneming?.ondernemingen || [];
        if (this.partnerBoolean) {
            ondernemingenLijst = this.partnerAangifte?.box1?.winstUitOnderneming?.ondernemingen || [];
        }

        const formArray = this.ondernemingFG.get('ondernemingen') as FormArray;
        const focusedControl = this.focusedElementId ? document.getElementById(this.focusedElementId) as HTMLElement : null;

        while (formArray.length > ondernemingenLijst.length) {
            formArray.removeAt(formArray.length - 1);
        }

        for (let i = 0; i < ondernemingenLijst.length; i++) {
            if (i < formArray.length) {
                const control = formArray.at(i) as FormGroup;
                control.patchValue(ondernemingenLijst[i], { emitEvent: false });
            } else {
                const formGroup = this.createOndernemingFormGroup(ondernemingenLijst[i]);
                formArray.push(formGroup);
            }
        }
        if (focusedControl && document.contains(focusedControl)) {
            focusedControl.focus();
        }
    }

    saveForm(): void {
        setTimeout(() => {
            this.ondernemingFG.updateValueAndValidity();
            const rawOndernemingen = this.ondernemingFG.value.ondernemingen;
            const sanitizedOndernemingen = this.sanitizeOndernemingen(rawOndernemingen);
            if (!this.partnerBoolean) {
                this.incomeTaxEntity.aangifte = {
                    ...this.aangifte,
                    box1: {
                        ...this.aangifte.box1,
                        winstUitOnderneming: {
                            ...this.aangifte.box1?.winstUitOnderneming,
                            ondernemingen: sanitizedOndernemingen
                        }
                    }
                } as Aangifte;
                this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
            } else {
                this.incomeTaxEntity.aangifte = {
                    ...this.aangifte,
                    partnerAangifte: {
                        ...this.aangifte.partnerAangifte,
                        box1: {
                            ...this.aangifte.partnerAangifte?.box1,
                            winstUitOnderneming: {
                                ...this.aangifte.partnerAangifte?.box1?.winstUitOnderneming,
                                ondernemingen: sanitizedOndernemingen
                            }
                        }
                    }
                } as Aangifte;
                this.dataService.updateIncomeTaxEntity(this.incomeTaxEntity);
            }
        }, 0);
    }

    private getRelevantAangifte(): Aangifte | undefined {
        return this.partnerBoolean
            ? this.incomeTaxEntity.aangifte?.partnerAangifte
            : this.incomeTaxEntity.aangifte;
    }

    private getOndernemingen(): Onderneming[] | undefined {
        const aangifte = this.getRelevantAangifte();
        return aangifte?.box1?.winstUitOnderneming?.ondernemingen;
    }

    public heeftOndernemingen(): boolean {
        const ondernemingen = this.getOndernemingen();
        return !!ondernemingen && ondernemingen.length > 0;
    }

    public heeftSamenwerkingsverband(): boolean {
        const ondernemingen = this.getOndernemingen();
        return !!ondernemingen?.[0]?.ondernemingsvorm &&
            ondernemingen[0].ondernemingsvorm !== Ondernemingsvorm.EENMANSZAAK;
    }

    sanitizeText(input: string): string {
        return input.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
    }

    sanitizeOndernemingen(ondernemingen: any[]): any[] {
        return ondernemingen.map(onderneming => ({
            ...onderneming,
            naam: this.sanitizeText(onderneming.naam)
            // Add more fields to sanitize if needed
        }));
    }

}


